import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';

interface UpliftProductMetric {
    metric_name: string;
    value: number;
}

interface UpliftProductPayload {
    dim1_id: number | string;
    metrics: UpliftProductMetric[];
}

interface UpliftRequestPayload {
    plan_id: string;
    event_id: string;
    dim2_attrs: string[];
    promo: {
        promo_id: string;
        attributes: Record<string, unknown>;
        offer: {
            method: string;
            params: Record<string, unknown>;
        };
        products: UpliftProductPayload[];
    };
}

interface UpliftPPGResponse {
    dim1_id: number;
    metrics: Record<string, number>;
}

interface UpliftResponse {
    results: UpliftPPGResponse[];
}

/** POST /tpo_gen/promos/model_uplift — returns predicted uplift % per PPG */
const fetchUpliftRecommendation = async (payload: UpliftRequestPayload): Promise<UpliftResponse> => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.getUpliftRecommendation, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        data: payload,
    });
    return response.data;
};

interface UseUpliftMutationOptions {
    onSuccess?: (data: UpliftResponse) => void;
}

/**
 * Mutation hook for the model uplift API.
 * Network-only concern — response consumption is handled by the caller (useUpliftTrigger).
 */
const useUpliftMutation = ({ onSuccess }: UseUpliftMutationOptions = {}) => {
    const { mutateAsync, isPending } = useMutation({
        mutationFn: fetchUpliftRecommendation,
        onSuccess,
        onError: (error: unknown) => {
            console.error('Uplift API Failed:', error);
        },
    });

    return { mutateAsync, isPending };
};

export type { UpliftRequestPayload, UpliftProductPayload, UpliftProductMetric, UpliftPPGResponse, UpliftResponse };
export default useUpliftMutation;
